import React, { useEffect, useState } from "react";
import { BsArrowLeft } from "react-icons/bs";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import {
  useGetAdminPlatformSettingsQuery,
  useUpdateAdminPlatformSettingsMutation,
} from "../../../store/adminPlatformSettingsApi";

function FormSettingsSkeleton() {
  return (
    <div className="space-y-5 w-full animate-pulse" aria-busy="true">
      <div className="h-4 bg-gray-200 rounded w-3/4 max-w-xl" />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="space-y-2">
            <div className="h-3 bg-gray-200 rounded w-32" />
            <div className="h-10 bg-gray-100 rounded-md w-full" />
          </div>
        ))}
      </div>
      <div className="space-y-2">
        <div className="h-3 bg-gray-200 rounded w-24" />
        <div className="h-10 bg-gray-100 rounded-md w-full max-w-md" />
      </div>
      <div className="h-11 bg-gray-200 rounded-md w-40" />
    </div>
  );
}

export default function PlatformSmsPricing() {
  const { data, isLoading, isError, error, isFetching } =
    useGetAdminPlatformSettingsQuery();
  const [save, { isLoading: isSaving }] =
    useUpdateAdminPlatformSettingsMutation();

  const [smsCharsPerPage, setSmsCharsPerPage] = useState("160");
  const [smsPricePerPage, setSmsPricePerPage] = useState("6");
  const [smsOnboardingNaira, setSmsOnboardingNaira] = useState("0");
  const [password, setPassword] = useState("");

  useEffect(() => {
    if (!data) return;
    setSmsCharsPerPage(String(data.sms_chars_per_page ?? 160));
    setSmsPricePerPage(String(data.sms_price_per_page ?? 6));
    setSmsOnboardingNaira(String(data.sms_onboarding_credit_naira ?? 0));
  }, [data]);

  const loadError =
    isError &&
    (error?.data?.message || error?.message || "Could not load settings.");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!password.trim()) {
      toast.error("Enter your password to save platform settings.");
      return;
    }
    const chars = parseInt(smsCharsPerPage, 10);
    const price = parseFloat(smsPricePerPage);
    const onboarding = parseFloat(smsOnboardingNaira);
    if (!Number.isFinite(chars) || chars < 1) {
      toast.error("SMS chars per page must be at least 1.");
      return;
    }
    if (!Number.isFinite(price) || price < 0.01) {
      toast.error("SMS price per page must be at least 0.01 NGN.");
      return;
    }
    if (!Number.isFinite(onboarding) || onboarding < 0) {
      toast.error("Onboarding credit must be 0 or greater.");
      return;
    }
    try {
      await save({
        sms_chars_per_page: chars,
        sms_price_per_page: price,
        sms_onboarding_credit_naira: onboarding,
        password,
      }).unwrap();
      toast.success("SMS billing settings updated.");
      setPassword("");
    } catch (err) {
      toast.error(err?.data?.message || err?.message || "Update failed.");
    }
  };

  const showSkeleton = isLoading && !data;

  return (
    <div className="p-6 bg-gray-50 min-h-screen w-full max-w-none">
      <div className="bg-white rounded-xl p-4 mb-6 flex items-center flex-wrap gap-3 w-full">
        <Link to="/sms-messaging" aria-label="Back to SMS messaging">
          <BsArrowLeft className="text-xl text-gray-600 cursor-pointer" />
        </Link>
        <h1 className="text-2xl font-bold text-[#45464e]">Platform SMS billing</h1>
      </div>

      {loadError && (
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6 text-red-600 text-sm w-full">
          {loadError}
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm p-6 w-full">
        {showSkeleton ? (
          <FormSettingsSkeleton />
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5 w-full max-w-none">
            <p className="text-gray-600 text-sm max-w-3xl">
              Configure how SMS is billed: characters per page, Naira per page,
              and the welcome SMS wallet credit (NGN) for new cooperatives&apos;
              first branch. Cooperatives add funds via online top-up. Confirm
              with your Uniquecoop admin password.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label
                  htmlFor="sms_chars_per_page"
                  className="block text-sm font-medium text-[#45464e] mb-1"
                >
                  SMS chars per page
                </label>
                <input
                  id="sms_chars_per_page"
                  type="number"
                  min="1"
                  value={smsCharsPerPage}
                  onChange={(ev) => setSmsCharsPerPage(ev.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-gray-800 bg-[#fafafa]"
                  disabled={isFetching}
                />
              </div>
              <div>
                <label
                  htmlFor="sms_price_per_page"
                  className="block text-sm font-medium text-[#45464e] mb-1"
                >
                  SMS price per page (NGN)
                </label>
                <input
                  id="sms_price_per_page"
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={smsPricePerPage}
                  onChange={(ev) => setSmsPricePerPage(ev.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-gray-800 bg-[#fafafa]"
                  disabled={isFetching}
                />
              </div>
              <div>
                <label
                  htmlFor="sms_onboarding_credit"
                  className="block text-sm font-medium text-[#45464e] mb-1"
                >
                  Onboarding SMS credit (NGN)
                </label>
                <input
                  id="sms_onboarding_credit"
                  type="number"
                  step="0.01"
                  min="0"
                  value={smsOnboardingNaira}
                  onChange={(ev) => setSmsOnboardingNaira(ev.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-gray-800 bg-[#fafafa]"
                  disabled={isFetching}
                />
              </div>
            </div>

            <div>
              <label
                htmlFor="admin_password_sms"
                className="block text-sm font-medium text-[#45464e] mb-1"
              >
                Your password
              </label>
              <input
                id="admin_password_sms"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(ev) => setPassword(ev.target.value)}
                className="w-full max-w-md border border-gray-300 rounded-md px-3 py-2 text-gray-800 bg-[#fafafa]"
                placeholder="Required to save"
              />
            </div>

            <button
              type="submit"
              disabled={isSaving || !!loadError}
              className="bg-[#8b313a] text-white py-2 px-6 rounded-md text-sm font-medium hover:bg-[#6b252a] transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? "Saving…" : "Save settings"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
